// src/RadarDisponibilitaPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Stack, Chip, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, TextField, InputAdornment
} from '@mui/material';
import RadarIcon from '@mui/icons-material/Radar';
import SearchIcon from '@mui/icons-material/Search';
import { fetchJson, authHeaders, API_BASE } from './api';

const toIsoDate = (s) => {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
};

export default function RadarDisponibilitaPage({ token, onSelectFreelancer }) {
  const [freelancers, setFreelancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(30); // giorni
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchJson('freelancers', { headers: authHeaders(token) });
        setFreelancers(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const today = useMemo(() => new Date(), []);

  const radarList = useMemo(() => {
    const inN = new Date(today.getTime() + window * 86400000);
    return freelancers
      .map(f => {
        const endRaw = f?.data_fine_contratto || f?.availability_end || '';
        if (!endRaw) return { ...f, _daysLeft: 0, _endDate: null };
        const ymd = String(endRaw).slice(0, 10);
        const [y, m, d] = ymd.split('-').map(Number);
        if (!y || !m || !d) return null;
        const endDate = new Date(y, m - 1, d, 12, 0, 0);
        if (endDate > inN) return null;
        const daysLeft = Math.ceil((endDate - today) / 86400000);
        return { ...f, _endDate: endDate, _daysLeft: Math.max(0, daysLeft) };
      })
      .filter(Boolean)
      .sort((a, b) => a._daysLeft - b._daysLeft);
  }, [freelancers, window, today]);

  const roles = useMemo(() => {
    const set = new Set();
    radarList.forEach(f => (f.ruoli || (f.ruolo ? [f.ruolo] : [])).forEach(r => set.add(r)));
    return [...set].sort();
  }, [radarList]);

  const filtered = useMemo(() => {
    let list = radarList;
    const editorGroup = ['Editor Video / Montatore', 'Editor Video / Montatore (Junior)', 'Editor Video / Montatore (Senior)'];
  if (roleFilter) {
    const filterRoles = roleFilter === 'Editor Video' ? editorGroup : [roleFilter];
    list = list.filter(f => (f.ruoli || [f.ruolo]).some(r => filterRoles.includes(r)));
  }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(f => `${f.nome} ${f.cognome}`.toLowerCase().includes(q));
    }
    return list;
  }, [radarList, roleFilter, search]);

  function resolveImg(u) {
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;
    return `${window.location?.origin || ''}${u.startsWith('/') ? '' : '/'}${u}`;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <RadarIcon sx={{ color: '#d4a550', fontSize: 22 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#f0ece4' }}>
          Radar Disponibilità
        </Typography>
        {!loading && (
          <Chip label={`${filtered.length} freelancer`} size="small"
            sx={{ bgcolor: 'rgba(212,165,80,0.1)', border: '1px solid rgba(212,165,80,0.3)', color: '#d4a550', fontWeight: 700 }} />
        )}
      </Stack>

      {/* Filtri */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel sx={{ color: '#888' }}>Finestra</InputLabel>
          <Select value={window} label="Finestra" onChange={e => setWindow(e.target.value)}
            sx={{ bgcolor: '#13161c', color: '#f0ece4', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
            <MenuItem value={7}>7 giorni</MenuItem>
            <MenuItem value={15}>15 giorni</MenuItem>
            <MenuItem value={30}>30 giorni</MenuItem>
            <MenuItem value={60}>60 giorni</MenuItem>
            <MenuItem value={90}>90 giorni</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ color: '#888' }}>Ruolo</InputLabel>
          <Select value={roleFilter} label="Ruolo" onChange={e => setRoleFilter(e.target.value)}
            sx={{ bgcolor: '#13161c', color: '#f0ece4', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
            <MenuItem value="">Tutti i ruoli</MenuItem>
            {[...(roles.some(r=>r.startsWith('Editor Video'))?['Editor Video']:[]),...roles.filter(r=>!r.startsWith('Editor Video'))].sort((a,b)=>a.localeCompare(b,'it')).map(r=><MenuItem key={r} value={r}>{r==='Editor Video'?'Editor Video (tutti)':r}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField size="small" placeholder="Cerca per nome..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#666' }} /></InputAdornment> }}
          sx={{ bgcolor: '#13161c', borderRadius: 2, '& .MuiOutlinedInput-root': { color: '#f0ece4', borderRadius: 2 } }}
        />
      </Stack>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#d4a550' }} />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <RadarIcon sx={{ fontSize: 52, color: '#2a2a2a', mb: 2 }} />
          <Typography sx={{ color: '#555', fontSize: 14 }}>
            Nessun freelancer in scadenza nei prossimi {window} giorni.
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', lg: 'repeat(3,1fr)' },
          gap: 1.5
        }}>
          {filtered.map((f, i) => {
            const name = `${f.nome || ''} ${f.cognome || ''}`.trim();
            const roles = f.ruoli?.length ? f.ruoli : f.ruolo ? [f.ruolo] : [];
            const isVeryClose = f._daysLeft <= 7;
            const imgUrl = f.profile_image_url ? `${API_BASE.replace('/api','')}/uploads/${f.profile_image_url.split('/').pop()}` : '';
            const initials = `${f.nome?.[0] || ''}${f.cognome?.[0] || ''}`.toUpperCase() || '?';

            return (
              <Box key={f.id ?? i} onClick={() => onSelectFreelancer?.(f.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  p: 2, borderRadius: 2, cursor: 'pointer',
                  bgcolor: '#13161c',
                  border: `1px solid ${f._daysLeft === 0 ? 'rgba(76,175,80,0.2)' : isVeryClose ? 'rgba(212,165,80,0.25)' : 'rgba(255,255,255,0.06)'}`,
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'rgba(212,165,80,0.4)', transform: 'translateY(-1px)' }
                }}>
                {/* Avatar */}
                <Box sx={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  bgcolor: '#1e2028', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 700, color: '#d4a550', fontFamily: 'Kanit, sans-serif'
                }}>
                  {f.profile_image_url
                    ? <Box component="img" src={resolveImg(f.profile_image_url)} alt={name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : initials}
                </Box>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#f0ece4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {name}
                  </Typography>
                  {roles[0] && (
                    <Typography sx={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {roles[0]}
                    </Typography>
                  )}
                </Box>

                {/* Badge giorni */}
                <Box sx={{
                  minWidth: 48, textAlign: 'center',
                  bgcolor: f._daysLeft === 0 ? 'rgba(76,175,80,0.15)' : isVeryClose ? 'rgba(212,165,80,0.15)' : 'rgba(255,255,255,0.05)',
                  borderRadius: 1.5, py: 0.5, px: 1,
                }}>
                  {f._daysLeft === 0 ? (
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#4caf50', lineHeight: 1.4 }}>Libero</Typography>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: isVeryClose ? '#d4a550' : '#aaa', lineHeight: 1 }}>
                        {f._daysLeft}
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: '#666', lineHeight: 1 }}>gg</Typography>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
